// review-prompt.ts — builds the AiGenerateRequest for the review co-writer (Phase 2.2).
// Pure — no network. Structured JSON output: { reflection, wins, blockers } so the
// AI fills all three review fields (not just the reflection).
// Feeds a compact ReviewSummary to Gemini; keeps context small and cost low.

import { formatMoney } from "../date";
import type { AiGenerateRequest } from "./ai-client";
import type { ReviewSummary } from "./review-summary";

// ── Result type + schema ───────────────────────────────────────────────────────

/** The three review fields the AI fills. */
export interface ReviewDraft {
  /** Main free-flowing reflection paragraph. */
  reflection: string;
  /** "Điểm sáng" — wins / what went well. */
  wins: string;
  /** "Rào cản" — blockers / what slipped. */
  blockers: string;
}

/**
 * Gemini responseSchema. NOTE (xem bài học 2.1): KHÔNG dùng `additionalProperties`
 * hay `minimum/maximum` — Gemini từ chối các khoá đó (400).
 */
export const REVIEW_DRAFT_SCHEMA: Record<string, unknown> = {
  type: "object",
  required: ["reflection", "wins", "blockers"],
  properties: {
    reflection: {
      type: "string",
      description: "Đoạn chiêm nghiệm chính: chia 2–3 ĐOẠN NGẮN (mỗi đoạn 2–3 câu), các đoạn CÁCH NHAU BẰNG MỘT DÒNG TRỐNG (hai ký tự xuống dòng \\n\\n). Ngôi thứ hai (bạn), ấm áp nhưng thực tế. KHÔNG đánh số, KHÔNG tiêu đề, KHÔNG viết thành một khối liền mạch.",
    },
    wins: {
      type: "string",
      description: "Điểm sáng / điều làm tốt — 1–3 ý, MỖI Ý MỘT DÒNG bắt đầu bằng '• ' và ngăn cách bằng \\n. Ví dụ: '• Giữ streak gym 4 buổi\\n• Hoàn thành báo cáo Q3'.",
    },
    blockers: {
      type: "string",
      description: "Rào cản / việc chưa trọn — 1–3 ý, MỖI Ý MỘT DÒNG bắt đầu bằng '• ' và ngăn cách bằng \\n. Nếu không có, ghi một dòng nhẹ nhàng (vd '• Không có rào cản đáng kể').",
    },
  },
};

// ── Prompt builder ────────────────────────────────────────────────────────────

/**
 * Assemble the structured AiGenerateRequest for the review co-writer.
 * PURE. Sends a compact summary (numbers + capped title list) — NOT raw rows.
 */
export function buildReviewRequest(summary: ReviewSummary): AiGenerateRequest {
  const { scope, periodLabel, stats, doneTitles } = summary;
  const scopeLabel = scope === "week" ? "tuần" : "tháng";
  const nextPeriodLabel = scope === "week" ? "tuần tới" : "tháng tới";

  const cashflowNote =
    stats.netCashflow >= 0
      ? `tích luỹ ${formatMoney(stats.netCashflow)} (thu > chi)`
      : `chi nhiều hơn thu ${formatMoney(Math.abs(stats.netCashflow))}`;

  const doneTitlesText =
    doneTitles.length > 0
      ? doneTitles.map((t, i) => `${i + 1}. ${t}`).join("\n")
      : "(không có dữ liệu)";

  const prompt = `Bạn là trợ lý cá nhân giúp người dùng viết nhật ký chiêm nghiệm cuối ${scopeLabel}. Hãy trả về JSON gồm 3 trường: reflection, wins, blockers — bằng tiếng Việt, xưng hô ngôi thứ hai (bạn), giọng ấm áp, khích lệ nhưng thực tế.

Dữ liệu ${scopeLabel} **${periodLabel}**:
- Việc hoàn thành: ${stats.doneCount}
- Việc dời lại: ${stats.deferredCount}
- Lần hoàn thành thói quen: ${stats.habitCompletions}
- Dòng tiền: ${cashflowNote}
- Mục tiêu đang theo dõi: ${stats.activeGoals}
- Dự án đang chạy: ${stats.activeProjects}

Một số việc đã hoàn thành ${scopeLabel} này:
${doneTitlesText}

- reflection: 2–3 ĐOẠN NGẮN (mỗi đoạn 2–3 câu), CÁCH NHAU BẰNG MỘT DÒNG TRỐNG (\\n\\n). Gợi ý mạch: (1) nhìn lại việc & cảm nhận; (2) dòng tiền (${cashflowNote}); (3) 1–2 trọng tâm ${nextPeriodLabel}. KHÔNG viết thành một khối liền.
- wins: 1–3 điểm sáng, MỖI Ý MỘT DÒNG bắt đầu bằng "• ", ngăn bằng \\n.
- blockers: 1–3 rào cản/việc chưa trọn, MỖI Ý MỘT DÒNG bắt đầu bằng "• ", ngăn bằng \\n (dựa số việc dời lại nếu có).

Đây là bản nháp để người dùng tự chỉnh — gợi mở, chân thực, TRÌNH BÀY THOÁNG, dễ đọc. Chỉ trả về JSON hợp lệ theo schema.`;

  return {
    contents: [{ role: "user", parts: [{ text: prompt }] }],
    generationConfig: { maxOutputTokens: 700, temperature: 0.6 },
    responseSchema: REVIEW_DRAFT_SCHEMA,
  };
}

/** Validate/normalize a raw model result into a safe ReviewDraft (never throws). */
export function mapReviewDraft(raw: Partial<ReviewDraft> | null | undefined): ReviewDraft {
  const s = (v: unknown): string => (typeof v === "string" ? v.trim() : "");
  return {
    reflection: s(raw?.reflection),
    wins: s(raw?.wins),
    blockers: s(raw?.blockers),
  };
}
